import { useEffect, useState, useCallback } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Chip from '@mui/material/Chip';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import { config } from '../config';
import { McpIcon } from '../components/icons';
import AccessStatusChip from '../components/AccessStatusChip';
import RequestAccessDialog from '../components/RequestAccessDialog';
import ToolsTable from '../components/ToolsTable';
import ServerUsersTab from '../components/ServerUsersTab';
import ServerLogsTab from '../components/ServerLogsTab';
import ConnectTabContent from '../components/ConnectTabContent';
import { api } from '../api';
import { PortalServerDetailOut as PortalServerDetail, PortalToolOut as PortalTool } from '../types/apiRequests';
import { useBeheerder } from '../hooks/useBeheerder';
import { useTabParam } from '../hooks/useTabParam';
import { useOrgInfo, useRefreshOrgInfo } from '../contexts/OrgInfoContext';
import { useTranslation } from '../i18n/useTranslation';
import { useServerUsers } from '../hooks/useServerUsers';
import { useServerLogs } from '../hooks/useServerLogs';

const TAB_ABOUT = 'about';
const TAB_CONNECT = 'connect';
const TAB_TOOLS = 'tools';
const TAB_SKILLS = 'skills';
const TAB_USERS = 'users';
const TAB_LOGS = 'logs';
const SERVER_TABS = [
  TAB_ABOUT,
  TAB_CONNECT,
  TAB_TOOLS,
  TAB_SKILLS,
  TAB_USERS,
  TAB_LOGS,
] as const;

export default function ServerDetailPage() {
  const { name } = useParams<{ name: string }>();
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently } = useOidcContext();
  const navigate = useNavigate();
  const t = useTranslation();
  const isBeheerder = useBeheerder();
  const orgInfo = useOrgInfo();
  const refreshOrgInfo = useRefreshOrgInfo();
  const isEnterprise = orgInfo?.hub_tier === 'enterprise';
  const [server, setServer] = useState<PortalServerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useTabParam(TAB_ABOUT, SERVER_TABS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const loadServer = useCallback(async () => {
    if (!name) return;
    try {
      const token = await getAccessTokenSilently();
      setServer(await api.getServerDetail(token, name));
    } catch {
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [name, getAccessTokenSilently, navigate]);

  const { serverUsers, usersLoading, usersLoaded, loadServerUsers, togglingUser, handleToggleUserAccess } = useServerUsers(name, isBeheerder, loadServer);
  const { serverLogs, logsLoading, logsLoaded, loadServerLogs } = useServerLogs(name, isBeheerder);

  useEffect(() => { loadServer(); }, [loadServer]);

  useEffect(() => {
    if (searchParams.get('action') === 'request-access' && server && !loading) {
      if (server.access_status === 'not_linked' || server.access_status === 'instant_available') {
        setDialogOpen(true);
      }
    }
  }, [searchParams, server, loading]);

  const handleSubmitRequest = useCallback(async (data: { request_type: string; environment_url?: string; environment_name?: string; message?: string }) => {
    if (!server) return;
    const token = await getAccessTokenSilently();
    await api.requestAccess(token, server.name, data);
    setDialogOpen(false);
    refreshOrgInfo();
    loadServer();
  }, [server, getAccessTokenSilently, refreshOrgInfo, loadServer]);

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleRemoveServer = useCallback(async () => {
    if (!server) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.removeServer(token, server.name);
      refreshOrgInfo();
      navigate('/');
    } catch (e: unknown) {
      setRemoveError(e instanceof Error ? e.message : t('errors.genericError'));
    } finally {
      setRemoving(false);
    }
  }, [server, getAccessTokenSilently, refreshOrgInfo, navigate, t]);

  useEffect(() => {
    if (tab === TAB_USERS && isBeheerder && !usersLoaded && !usersLoading) {
      loadServerUsers();
    }
  }, [tab, isBeheerder, usersLoaded, usersLoading, loadServerUsers]);

  useEffect(() => {
    if (tab === TAB_LOGS && isBeheerder && !logsLoaded && !logsLoading) {
      loadServerLogs();
    }
  }, [tab, isBeheerder, logsLoaded, logsLoading, loadServerLogs]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={140} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (!server) {
    return (
      <Box sx={{ textAlign: 'center', py: 12 }}>
        <McpIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }} />
        <Typography sx={{ fontWeight: 600, color: 'text.secondary', mb: 2 }}>{t('serverDetail.notFound.title')}</Typography>
        <Button onClick={() => navigate('/')} variant="outlined" size="small">{t('common.backToOverview')}</Button>
      </Box>
    );
  }

  const serverUrl = `${config.api.mcpBaseUrl}/api/v1/mcp/${server.name}/`;

  // Tabs actually rendered for this server + role; a `?tab=` value pointing at a
  // hidden tab falls back to About so the indicator stays in sync.
  const visibleTabs: string[] = [
    TAB_ABOUT,
    ...(isBeheerder || server.access_status === 'full_access' ? [TAB_CONNECT] : []),
    ...(isBeheerder ? [TAB_TOOLS, TAB_SKILLS, TAB_USERS, TAB_LOGS] : []),
  ];
  const selectedTab = visibleTabs.includes(tab) ? tab : TAB_ABOUT;

  const toolsByTag: Record<string, PortalTool[]> = {};
  for (const tool of (server.tools ?? [])) {
    const tag = tool.tag || 'Overig';
    if (!toolsByTag[tag]) toolsByTag[tag] = [];
    toolsByTag[tag].push(tool);
  }

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
        onClick={() => navigate('/')}
        sx={{ mb: 3, color: 'text.secondary', fontSize: '0.85rem', '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' } }}
      >
        {t('serverDetail.backButton')}
      </Button>
      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 4, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '14px',
                bgcolor: server.access_status === 'full_access' ? 'primary.main' : 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <McpIcon
                sx={{
                  fontSize: 26,
                  color: server.access_status === 'full_access' ? '#fff' : '#71717a',
                }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ mb: 0.25 }}>
                {server.display_name}
              </Typography>
              {server.application_name && (
                <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem', fontWeight: 500, mb: 0.5 }}>
                  {server.application_name}
                </Typography>
              )}
              <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                {server.description || t('common.noDescription')}
              </Typography>
              {server.active_environment_display_name && (
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={server.active_environment_display_name}
                    size="small"
                    variant="outlined"
                    color="success"
                    sx={{ fontWeight: 500, fontSize: '0.75rem', height: 22 }}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <AccessStatusChip status={server.access_status} size="medium" />
              {(server.access_status === 'full_access' || server.access_status === 'org_linked_no_user') && isBeheerder && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  disabled={removing}
                  onClick={() => setRemoveDialogOpen(true)}
                >
                  {removing ? t('serverDetail.removing') : t('serverDetail.remove')}
                </Button>
              )}
              {isBeheerder && (server.access_status === 'not_linked' || server.access_status === 'instant_available') && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setDialogOpen(true)}
                  sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'text.primary' } }}
                >
                  {t('serverDetail.header.activateMcp')}
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
              {t('serverDetail.tabs.tools', { count: server.tool_count ?? 0 })}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
              {t('serverDetail.tabs.skills', { count: server.skill_count ?? 0 })}
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={selectedTab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 4,
            borderTop: '1px solid rgba(0,0,0,0.05)',
            '& .MuiTabs-indicator': {
              height: 2,
              borderRadius: '2px 2px 0 0',
              bgcolor: 'primary.main',
            },
          }}
        >
          <Tab label={t('serverDetail.tabs.about')} value={TAB_ABOUT} />
          {(isBeheerder || server.access_status === 'full_access') && <Tab label={t('serverDetail.tabs.connect')} value={TAB_CONNECT} />}
          {isBeheerder && <Tab label={t('serverDetail.tabs.tools', { count: server.tool_count ?? 0 })} value={TAB_TOOLS} />}
          {isBeheerder && <Tab label={t('serverDetail.tabs.skills', { count: server.skill_count ?? 0 })} value={TAB_SKILLS} />}
          {isBeheerder && <Tab label={t('serverDetail.tabs.users')} value={TAB_USERS} icon={<GroupIcon sx={{ fontSize: 16 }} />} iconPosition="start" />}
          {isBeheerder && <Tab label={t('serverDetail.tabs.logs')} value={TAB_LOGS} icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" />}
        </Tabs>
      </Card>
      {server.blocked_until && new Date(server.blocked_until) > new Date() && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
          {t('serverDetail.blocked', {
            until: new Date(server.blocked_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          })}
        </Alert>
      )}
      {tab === TAB_ABOUT && (
        <Card sx={{ p: 4 }}>
          {server.about ? (
            <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: 'text.primary' }}>
              {server.about}
            </Typography>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                {t('serverDetail.about.empty')}
              </Typography>
            </Box>
          )}
        </Card>
      )}
      {tab === TAB_CONNECT && (isBeheerder || server.access_status === 'full_access') && (
        <>
          <ConnectTabContent
            server={server}
            serverUrl={serverUrl}
            copied={copied}
            onCopy={handleCopy}
            isBeheerder={isBeheerder}
            t={t}
          />
        </>
      )}
      {tab === TAB_TOOLS && isBeheerder && (
        <ToolsTable tools={(server.tools ?? [])} toolsByTag={toolsByTag} />
      )}
      {tab === TAB_SKILLS && isBeheerder && (
        <Card sx={{ overflow: 'hidden' }}>
          {(server.skills ?? []).length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.disabled' }}>{t('serverDetail.skills.empty')}</Typography>
            </Box>
          ) : (
            (server.skills ?? []).map((skill, i) => (
              <Box key={skill.id} sx={{ borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        {skill.name}
                      </Typography>
                      {skill.description && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                          {skill.description}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title={copied ? t('common.copied') : t('common.copy')}>
                      <IconButton size="small" onClick={() => handleCopy(skill.content)} sx={{ color: 'text.disabled' }}>
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: '#fafafa',
                      borderRadius: '8px',
                      border: '1px solid rgba(0,0,0,0.05)',
                      p: 2,
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '0.78rem',
                        color: 'text.primary',
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.7,
                      }}
                    >
                      {skill.content}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Card>
      )}
      {tab === TAB_USERS && isBeheerder && (
        <ServerUsersTab
          users={serverUsers}
          loading={usersLoading}
          togglingUser={togglingUser}
          onToggleAccess={handleToggleUserAccess}
        />
      )}
      {tab === TAB_LOGS && isBeheerder && (
        <ServerLogsTab logs={serverLogs} loading={logsLoading} />
      )}

      {server && (
        <RequestAccessDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmitRequest}
          serverName={server.display_name}
          hasDefaultUrl={server.has_default_url ?? false}
          environments={server.environments ?? []}
        />
      )}

      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('serverDetail.remove')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('serverDetail.removeConfirm')}
          </Typography>
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }}>{removeError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveDialogOpen(false)} color="inherit">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleRemoveServer} color="error" variant="contained" disabled={removing}>
            {removing ? t('serverDetail.removing') : t('serverDetail.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
